import { Checkbox } from '@progress/kendo-react-inputs';
import {
  TreeList,
  TreeListColumnProps,
  TreeListExpandChangeEvent,
  TreeListRowClickEvent,
  TreeListRowProps,
  extendDataItem,
  mapTree,
} from '@progress/kendo-react-treelist';
import { FC, ReactElement, cloneElement, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import useAuth from 'src/hooks/useAuth';
import useWindowSize from 'src/hooks/useWindowSize';
import {
  workAreaCompanyPanelHeaderClassName,
  workAreaCompanyPanelTitleClassName,
  workAreaPanelBodyClassName,
  workAreaPanelHeaderClassName,
  workAreaPanelTitleClassName,
} from 'src/sections/work_area/workAreaPanelLayout';
import {
  computeShareRecipientDiff,
  mapFavoriteClientsToTree,
  mapFavoriteCompaniesToTree,
} from 'src/services/favorite/mapFavoriteShareCopyTree';
import useGetFavoriteClientsQuery from 'src/services/favorite/useGetFavoriteClientsQuery';
import useGetFavoriteCompaniesQuery from 'src/services/favorite/useGetFavoriteCompaniesQuery';
import {
  ShareCopyCompanyNode,
  ShareCopyListMode,
  ShareCopySelectionState,
  ShareCopyUserNode,
} from 'src/types/view_shopping_lists/ShareCopyList';
import { convertToDate } from 'src/utils/formatTime';
import { getShareCopyOverflowTooltipProps } from './shareCopyOverflowTooltip';

interface CompanyRow {
  id: number;
  name: string;
  companies?: CompanyRow[];
  expanded?: boolean;
  selected?: boolean;
}

interface UserRow {
  id: number;
  contact: string;
  lastSent: string;
  isSelectionLocked: boolean;
  selected: boolean;
  subUsers?: UserRow[];
  expanded?: boolean;
}

const COMPANY_SUB_ITEMS = 'companies';
const USER_SUB_ITEMS = 'subUsers';
const EXPAND_FIELD = 'expanded';

const EMPTY_SELECTION: ShareCopySelectionState = {
  selectedUserIds: [],
  folderAddUsers: [],
  folderRemoveUsers: [],
};

const mapCompanyNode = (node: ShareCopyCompanyNode): CompanyRow => ({
  id: node.companyId,
  name: node.companyName,
  companies: node.children?.map(mapCompanyNode) ?? [],
});

const mapUserNode = (
  node: ShareCopyUserNode,
  selectedUserIds: ReadonlySet<number>,
  isCopy: boolean
): UserRow => ({
  id: node.clientId,
  contact: node.contact,
  lastSent: convertToDate((isCopy ? node.lastCopiedDate : node.lastSharedDate) ?? ''),
  isSelectionLocked: node.isSelectionLocked,
  // Locked recipients stay visually checked and cannot be toggled off.
  selected: node.isSelectionLocked || selectedUserIds.has(node.clientId),
  subUsers: node.children?.map((child) => mapUserNode(child, selectedUserIds, isCopy)) ?? [],
});

const collectExpandableCompanyIds = (items: CompanyRow[]): number[] =>
  items.flatMap((item) => [
    ...(item.companies?.length ? [item.id] : []),
    ...(item.companies ? collectExpandableCompanyIds(item.companies) : []),
  ]);

const collectExpandableUserIds = (items: UserRow[]): number[] =>
  items.flatMap((item) => [
    ...(item.subUsers?.length ? [item.id] : []),
    ...(item.subUsers ? collectExpandableUserIds(item.subUsers) : []),
  ]);

const collectSelectedUserIds = (items: UserRow[]): number[] => {
  const selectedIds: number[] = [];
  const walk = (rows: UserRow[]) => {
    rows.forEach((row) => {
      if (row.selected && !row.isSelectionLocked) {
        selectedIds.push(row.id);
      }
      if (row.subUsers?.length) {
        walk(row.subUsers);
      }
    });
  };
  walk(items);
  return selectedIds;
};

const collectAllUserIds = (nodes: ShareCopyUserNode[]): number[] => {
  const ids: number[] = [];
  const walk = (rows: ShareCopyUserNode[]) => {
    rows.forEach((row) => {
      ids.push(row.clientId);
      if (row.children?.length) {
        walk(row.children);
      }
    });
  };
  walk(nodes);
  return ids;
};

const collectPreselectedUserIds = (nodes: ShareCopyUserNode[]): number[] => {
  const selectedIds: number[] = [];
  const walk = (rows: ShareCopyUserNode[]) => {
    rows.forEach((row) => {
      if (row.isPreSelected) {
        selectedIds.push(row.clientId);
      }
      if (row.children?.length) {
        walk(row.children);
      }
    });
  };
  walk(nodes);
  return selectedIds;
};

const collectVisibleUserIds = (items: UserRow[]): Set<number> => {
  const visibleIds = new Set<number>();
  const walk = (rows: UserRow[]) => {
    rows.forEach((row) => {
      visibleIds.add(row.id);
      if (row.subUsers?.length) {
        walk(row.subUsers);
      }
    });
  };
  walk(items);
  return visibleIds;
};

const updateUserSelection = (items: UserRow[], userId: number, selected: boolean): UserRow[] =>
  items.map((item) => {
    if (item.id === userId) {
      if (item.isSelectionLocked) {
        return item;
      }
      return { ...item, selected };
    }
    if (!item.subUsers?.length) {
      return item;
    }
    return { ...item, subUsers: updateUserSelection(item.subUsers, userId, selected) };
  });

const setAllUserSelection = (items: UserRow[], selected: boolean): UserRow[] =>
  items.map((item) => ({
    ...item,
    // Locked rows always remain checked; select/deselect all only affects unlocked rows.
    selected: item.isSelectionLocked ? true : selected,
    subUsers: item.subUsers ? setAllUserSelection(item.subUsers, selected) : undefined,
  }));

const countSelectedUsers = (
  items: UserRow[]
): { total: number; locked: number; selectableSelected: number } => {
  let total = 0;
  let locked = 0;
  let selectableSelected = 0;

  const walk = (rows: UserRow[]) => {
    rows.forEach((row) => {
      total += 1;
      if (row.isSelectionLocked) {
        locked += 1;
      } else if (row.selected) {
        selectableSelected += 1;
      }
      if (row.subUsers?.length) {
        walk(row.subUsers);
      }
    });
  };

  walk(items);
  return { total, locked, selectableSelected };
};

const findCompanyIdInTree = (items: CompanyRow[], id: number): boolean => {
  for (const item of items) {
    if (item.id === id) {
      return true;
    }
    if (item.companies?.length && findCompanyIdInTree(item.companies, id)) {
      return true;
    }
  }
  return false;
};

const findCompanyName = (items: CompanyRow[], id: number): string | null => {
  for (const item of items) {
    if (item.id === id) {
      return item.name;
    }
    if (item.companies?.length) {
      const found = findCompanyName(item.companies, id);
      if (found) {
        return found;
      }
    }
  }
  return null;
};

const companyColumns: TreeListColumnProps[] = [
  { field: 'name', title: '', width: '100%', expandable: true },
];

export interface ShareCopyListRecipientsContentProps {
  mode: ShareCopyListMode;
  folderIds: number[];
  onSelectionChange: (selection: ShareCopySelectionState) => void;
}

const ShareCopyListRecipientsContent: FC<ShareCopyListRecipientsContentProps> = ({
  mode,
  folderIds,
  onSelectionChange,
}) => {
  const { t } = useTranslation();
  const { width: viewportWidth } = useWindowSize();
  const { clientId, companyId } = useAuth();
  const lockedUserIds = useMemo(() => {
    const parsedClientId = Number(clientId);
    return Number.isFinite(parsedClientId) ? new Set([parsedClientId]) : new Set<number>();
  }, [clientId]);

  const defaultCompanyId = useMemo(() => {
    const parsedCompanyId = Number(companyId);
    return Number.isFinite(parsedCompanyId) ? parsedCompanyId : null;
  }, [companyId]);

  const folderIdForClients = folderIds.length === 1 ? folderIds[0] : undefined;
  const isCopy = mode === 'copy';

  const {
    data: companiesResponse,
    isLoading: isCompaniesLoading,
    isError: isCompaniesError,
  } = useGetFavoriteCompaniesQuery(true);

  const companyTreeData = useMemo(
    () => mapFavoriteCompaniesToTree(companiesResponse?.rows ?? []).map(mapCompanyNode),
    [companiesResponse?.rows]
  );

  const [selectedCompanyId, setSelectedCompanyId] = useState<number | null>(null);
  const [expandedCompanyIds, setExpandedCompanyIds] = useState<number[]>([]);
  const [userRows, setUserRows] = useState<UserRow[]>([]);
  const [expandedUserIds, setExpandedUserIds] = useState<number[]>([]);

  const selectedUserIdsRef = useRef<number[]>([]);
  const initialSharedUserIdsRef = useRef<number[]>([]);
  const knownUserIdsRef = useRef<Set<number>>(new Set());
  const hasInitializedCompanyRef = useRef(false);
  const onSelectionChangeRef = useRef(onSelectionChange);
  onSelectionChangeRef.current = onSelectionChange;

  const clientsQueryParams = useMemo(
    () =>
      selectedCompanyId == null
        ? null
        : {
            companyId: selectedCompanyId,
            isCopy,
            folderId: folderIdForClients,
          },
    [folderIdForClients, isCopy, selectedCompanyId]
  );

  const {
    data: clientsResponse,
    isLoading: isClientsLoading,
    isError: isClientsError,
  } = useGetFavoriteClientsQuery(clientsQueryParams, selectedCompanyId != null);

  const emitSelection = useCallback((nextSelectedUserIds: number[], nextInitialSharedUserIds: number[]) => {
    const uniqueSelected = Array.from(new Set(nextSelectedUserIds)).filter(
      (id) => !lockedUserIds.has(id)
    );
    const uniqueInitial = Array.from(new Set(nextInitialSharedUserIds)).filter(
      (id) => !lockedUserIds.has(id)
    );
    selectedUserIdsRef.current = uniqueSelected;
    initialSharedUserIdsRef.current = uniqueInitial;

    // Share and copy both treat any deviation from the original baseline as a change.
    const { folderAddUsers, folderRemoveUsers } = computeShareRecipientDiff(
      uniqueInitial,
      uniqueSelected
    );
    onSelectionChangeRef.current({
      selectedUserIds: uniqueSelected,
      folderAddUsers,
      folderRemoveUsers,
    });
  }, [lockedUserIds]);

  const folderIdsKey = folderIds.join(',');

  useEffect(() => {
    knownUserIdsRef.current = new Set();
    hasInitializedCompanyRef.current = false;
    selectedUserIdsRef.current = [];
    initialSharedUserIdsRef.current = [];
    setSelectedCompanyId(null);
    setExpandedCompanyIds([]);
    setUserRows([]);
    setExpandedUserIds([]);
    onSelectionChangeRef.current(EMPTY_SELECTION);
  }, [folderIdsKey, mode]);

  useEffect(() => {
    if (!companyTreeData.length || hasInitializedCompanyRef.current) {
      return;
    }
    hasInitializedCompanyRef.current = true;
    const preferredCompanyId =
      defaultCompanyId != null && findCompanyIdInTree(companyTreeData, defaultCompanyId)
        ? defaultCompanyId
        : companyTreeData[0].id;
    setSelectedCompanyId(preferredCompanyId);
    setExpandedCompanyIds(collectExpandableCompanyIds(companyTreeData));
  }, [companyTreeData, defaultCompanyId]);

  useEffect(() => {
    if (selectedCompanyId == null) {
      setUserRows([]);
      setExpandedUserIds([]);
      return;
    }

    if (!clientsResponse) {
      return;
    }

    const clientRows =
      folderIdForClients == null
        ? (clientsResponse.rows ?? []).map((row) => ({ ...row, enabled: false }))
        : (clientsResponse.rows ?? []);

    const clientTree = mapFavoriteClientsToTree(clientRows, lockedUserIds);
    const allUserIds = collectAllUserIds(clientTree);
    const newlySeenPreselected = collectPreselectedUserIds(clientTree).filter(
      (id) => !knownUserIdsRef.current.has(id)
    );

    allUserIds.forEach((id) => knownUserIdsRef.current.add(id));

    let nextSelected = selectedUserIdsRef.current;
    let nextInitial = initialSharedUserIdsRef.current;

    if (newlySeenPreselected.length > 0) {
      nextSelected = Array.from(new Set([...nextSelected, ...newlySeenPreselected]));
      nextInitial = Array.from(new Set([...nextInitial, ...newlySeenPreselected]));
      emitSelection(nextSelected, nextInitial);
    }

    const selectedSet = new Set(nextSelected);
    const mappedUsers = clientTree.map((node) => mapUserNode(node, selectedSet, isCopy));
    setUserRows(mappedUsers);
    setExpandedUserIds(collectExpandableUserIds(mappedUsers));
  }, [clientsResponse, emitSelection, folderIdForClients, isCopy, lockedUserIds, mode, selectedCompanyId]);

  const selectedCompanyName = useMemo(() => {
    if (selectedCompanyId == null) {
      return '';
    }
    return findCompanyName(companyTreeData, selectedCompanyId) ?? '';
  }, [companyTreeData, selectedCompanyId]);

  const processedCompanies = useMemo(
    () =>
      mapTree(companyTreeData, COMPANY_SUB_ITEMS, (item) =>
        extendDataItem(item, COMPANY_SUB_ITEMS, {
          [EXPAND_FIELD]: expandedCompanyIds.includes(item.id),
          selected: item.id === selectedCompanyId,
        })
      ),
    [companyTreeData, expandedCompanyIds, selectedCompanyId]
  );

  const processedUsers = useMemo(
    () =>
      mapTree(userRows, USER_SUB_ITEMS, (item) =>
        extendDataItem(item, USER_SUB_ITEMS, {
          [EXPAND_FIELD]: expandedUserIds.includes(item.id),
        })
      ),
    [expandedUserIds, userRows]
  );

  const selectionSummary = useMemo(() => countSelectedUsers(userRows), [userRows]);

  const handleCompanyExpandChange = useCallback((event: TreeListExpandChangeEvent) => {
    const companyId = event.dataItem.id as number;
    setExpandedCompanyIds((prev) =>
      event.value ? prev.filter((id) => id !== companyId) : [...prev, companyId]
    );
  }, []);

  const handleCompanyRowClick = useCallback((event: TreeListRowClickEvent) => {
    setSelectedCompanyId(event.dataItem.id as number);
  }, []);

  const handleUserExpandChange = useCallback((event: TreeListExpandChangeEvent) => {
    const userId = event.dataItem.id as number;
    setExpandedUserIds((prev) =>
      event.value ? prev.filter((id) => id !== userId) : [...prev, userId]
    );
  }, []);

  const applyUserRows = useCallback(
    (nextRows: UserRow[]) => {
      const visibleIds = collectVisibleUserIds(userRows);
      const preservedFromOtherCompanies = selectedUserIdsRef.current.filter(
        (id) => !visibleIds.has(id)
      );
      const mergedSelected = Array.from(
        new Set([...preservedFromOtherCompanies, ...collectSelectedUserIds(nextRows)])
      );

      setUserRows(nextRows);
      emitSelection(mergedSelected, initialSharedUserIdsRef.current);
    },
    [emitSelection, userRows]
  );

  const handleUserCheckboxChange = useCallback(
    (userId: number, checked: boolean) => {
      applyUserRows(updateUserSelection(userRows, userId, checked));
    },
    [applyUserRows, userRows]
  );

  const selectableTotal = selectionSummary.total - selectionSummary.locked;
  const isAllSelectableSelected =
    selectableTotal > 0 && selectionSummary.selectableSelected === selectableTotal;

  const handleSelectAllChange = useCallback(() => {
    applyUserRows(setAllUserSelection(userRows, !isAllSelectableSelected));
  }, [applyUserRows, isAllSelectableSelected, userRows]);

  const userColumns: TreeListColumnProps[] = useMemo(
    () => [
      {
        field: 'contact',
        title: t('userNameAndRole'),
        expandable: true,
        className: 'share-copy-users-contact-cell',
        headerClassName: 'share-copy-users-contact-cell',
      },
      ...(isCopy
        ? [
            {
              field: 'lastSent',
              title: t('lastSent'),
              width: 100,
              sortable: false,
              className: 'share-copy-users-last-sent-cell',
              headerClassName: 'share-copy-users-last-sent-cell',
            } satisfies TreeListColumnProps,
          ]
        : []),
      {
        field: 'selected',
        title: ' ',
        width: 56,
        sortable: false,
        className: 'share-copy-users-checkbox-cell',
        headerClassName: 'share-copy-users-checkbox-cell',
        headerCell: () => (
          <div className="flex w-full items-center justify-center">
            <Checkbox
              checked={isAllSelectableSelected}
              onChange={handleSelectAllChange}
              disabled={selectableTotal === 0}
              aria-label={t('Select All')}
            />
          </div>
        ),
        cell: (props) => {
          const dataItem = props.dataItem as UserRow;
          return (
            <td
              className={`k-table-td share-copy-users-checkbox-cell !w-14 !min-w-14 !max-w-14 !px-1${
                dataItem.isSelectionLocked ? ' share-copy-users-checkbox-cell--locked' : ''
              }`}
            >
              <div className="flex items-center justify-center">
                <Checkbox
                  checked={dataItem.selected}
                  disabled={dataItem.isSelectionLocked}
                  className={
                    dataItem.isSelectionLocked ? 'share-copy-users-checkbox--locked' : undefined
                  }
                  onChange={(event) =>
                    handleUserCheckboxChange(dataItem.id, Boolean(event.value))
                  }
                  aria-label={dataItem.contact}
                />
              </div>
            </td>
          );
        },
      },
    ],
    [
      handleSelectAllChange,
      handleUserCheckboxChange,
      isAllSelectableSelected,
      isCopy,
      selectableTotal,
      t,
    ]
  );

  const usersPanelTitle = selectedCompanyName
    ? `${t('users')} - ${selectedCompanyName}`
    : t('users');

  const userRowRender = useCallback((row: ReactElement<HTMLTableRowElement>, props: TreeListRowProps) => {
    const dataItem = props.dataItem as UserRow;
    if (!dataItem.isSelectionLocked) {
      return row;
    }

    return cloneElement(row, {
      className: `${row.props.className ?? ''} share-copy-users-row--locked`.trim(),
    });
  }, []);

  const companyCellRender = useCallback(
    (defaultRendering: ReactElement | null, props: { field?: string; dataItem?: CompanyRow }) => {
      if (!defaultRendering || props.field !== 'name') {
        return defaultRendering;
      }

      const name = props.dataItem?.name ?? '';
      const tooltipProps = getShareCopyOverflowTooltipProps(name, viewportWidth, 'cell');
      if (!tooltipProps) {
        return defaultRendering;
      }

      return cloneElement(defaultRendering, tooltipProps as Partial<ReactElement['props']>);
    },
    [viewportWidth]
  );

  const userCellRender = useCallback(
    (defaultRendering: ReactElement | null, props: { field?: string; dataItem?: UserRow }) => {
      if (!defaultRendering || (props.field !== 'contact' && props.field !== 'lastSent')) {
        return defaultRendering;
      }

      const text =
        props.field === 'lastSent'
          ? (props.dataItem?.lastSent ?? '')
          : (props.dataItem?.contact ?? '');
      const tooltipProps = getShareCopyOverflowTooltipProps(
        text,
        viewportWidth,
        'cell',
        props.field === 'lastSent'
      );
      if (!tooltipProps) {
        return defaultRendering;
      }

      return cloneElement(defaultRendering, tooltipProps as Partial<ReactElement['props']>);
    },
    [viewportWidth]
  );

  const renderUsersBody = () => {
    if (selectedCompanyId == null) {
      return (
        <div className="flex h-full items-center justify-center p-4 text-sm text-gray-500">
          {t('selectCompanyToViewUsers')}
        </div>
      );
    }

    if (isClientsLoading) {
      return (
        <div className="flex h-full items-center justify-center p-4 text-sm text-gray-500">
          {t('Loading...')}
        </div>
      );
    }

    if (isClientsError) {
      return (
        <div className="flex h-full items-center justify-center p-4 text-sm text-gray-500">
          {t('Failed to load data')}
        </div>
      );
    }

    return (
      <TreeList
        className="share-copy-users-treelist"
        style={{ width: '100%', maxWidth: '100%' }}
        data={processedUsers}
        expandField={EXPAND_FIELD}
        subItemsField={USER_SUB_ITEMS}
        columns={userColumns}
        onExpandChange={handleUserExpandChange}
        rowRender={userRowRender}
        cellRender={userCellRender}
        rowHeight={36}
      />
    );
  };

  return (
    <div
      className="share-copy-list-recipients grid h-full min-h-0 w-full flex-1 grid-cols-[45fr_55fr] items-stretch gap-2 overflow-hidden px-4 pb-4 max-[900px]:grid-cols-1 max-[900px]:grid-rows-[minmax(12rem,1fr)_minmax(16rem,1.2fr)] max-[900px]:gap-3 max-[900px]:overflow-y-auto"
      data-testid="share-copy-list-recipients"
    >
      <div className="work-area-panel work-area-company-panel flex h-full min-h-0 min-w-0 w-full flex-col border border-gray-300 max-[900px]:min-h-[12rem]">
        <div className={workAreaCompanyPanelHeaderClassName}>
          <h2 className={workAreaCompanyPanelTitleClassName}>{t('companyTree')}</h2>
        </div>
        <div className={`${workAreaPanelBodyClassName} company-tree-list`}>
          {isCompaniesLoading ? (
            <div className="flex h-full items-center justify-center p-4 text-sm text-gray-500">
              {t('Loading...')}
            </div>
          ) : isCompaniesError ? (
            <div className="flex h-full items-center justify-center p-4 text-sm text-gray-500">
              {t('Failed to load data')}
            </div>
          ) : (
            <TreeList
              data={processedCompanies}
              expandField={EXPAND_FIELD}
              subItemsField={COMPANY_SUB_ITEMS}
              columns={companyColumns}
              selectedField="selected"
              onExpandChange={handleCompanyExpandChange}
              onRowClick={handleCompanyRowClick}
              cellRender={companyCellRender}
              rowHeight={36}
            />
          )}
        </div>
      </div>

      <div className="work-area-panel work-area-users-panel flex h-full min-h-0 min-w-0 w-full flex-col border border-gray-300 max-[900px]:min-h-[16rem]">
        <div className={workAreaPanelHeaderClassName}>
          <h2
            className={workAreaPanelTitleClassName}
            {...getShareCopyOverflowTooltipProps(usersPanelTitle, viewportWidth, 'cell')}
          >
            {usersPanelTitle}
          </h2>
        </div>
        <div
          className={`${workAreaPanelBodyClassName} user-companies-list${
            isCopy ? ' share-copy-users-list--copy' : ''
          }`}
        >
          {renderUsersBody()}
        </div>
      </div>
    </div>
  );
};

export default ShareCopyListRecipientsContent;
